import LTVCalculators from '/src/services/LTVCalculators';
import CalculateAnnualMortgageCost from '/src/services/CalculateAnnualMortgageCost';

export default function RQSAOpex(propertyData, userPersonalInfo, geoData) {

    // console.log('userPersonalInfo', userPersonalInfo);
    
    // Pull the key information from the userPersonalInfo object
    const userFinancesHouseholdIncome = userPersonalInfo.householdIncome;
    const userFinancesAvailableFunds = userPersonalInfo.availableFunds;
    const userFinancesRelationToProperty = userPersonalInfo.relationToProperty;
    const userFinancesMonthlyMortgagePayment = userPersonalInfo.monthlyMortgagePayment;
    const userFinancesExpectedPurchasePrice = userPersonalInfo.expectedPurchasePrice;

    // Determine the annual financing costs for the property based on if a buyer or owner
    let annualFinancingCosts = 0;
    if (userFinancesRelationToProperty === "buyer") {
        annualFinancingCosts = CalculateAnnualMortgageCost(userFinancesExpectedPurchasePrice);
    } else if (userFinancesRelationToProperty === "owner") {
        annualFinancingCosts = userFinancesMonthlyMortgagePayment * 12;
    }
    // console.log('Annual Financing Costs',userFinancesRelationToProperty,  annualFinancingCosts);

    // Pull the key information from the propertyData object
    const financialData = propertyData?.data?.scenario_modeling?.individual_breakdowns?.[0]?.financials_no_improvements_made;
    // Pull the key information from the geoData object
    const fipsData = geoData.fips
    const zipData = geoData.zip
    
    // Pull the key information from the financialData object
    const financialDataBreakdown = financialData?.breakdown;
    const opex10YearBreakdown = financialDataBreakdown?.opex;
    // console.log('opex10YearBreakdown', opex10YearBreakdown);
    
    // Take the 10 year breakdown array, get the annual_total array, and get just the values
    const opex10YearBreakdownAnnualTotal = opex10YearBreakdown.annual_total.map(item => (item.value + annualFinancingCosts));
    // console.log('Opex 10 Year Breakdown Annual Total', opex10YearBreakdownAnnualTotal);

    // Get the mean and median household income for the fips and zip for 2021 and 2023
    const fipsCurrentIncomeMedian_2021 = fipsData.census_income_data['2021'].median_household_income;
    const fipsCurrentIncomeMean_2021 = fipsData.census_income_data['2021'].mean_household_income;
    const zipCurrentIncomeMedian_2021 = zipData.census_income_data['2021'].median_household_income;
    const zipCurrentIncomeMean_2021 = zipData.census_income_data['2021'].mean_household_income;
    const fipsCurrentIncomeMedian_2023 = fipsData.census_income_data['2023'].median_household_income;
    const fipsCurrentIncomeMean_2023 = fipsData.census_income_data['2023'].mean_household_income;
    const zipCurrentIncomeMedian = zipData.census_income_data['2023'].median_household_income;
    const zipCurrentIncomeMean = zipData.census_income_data['2023'].mean_household_income;

    // Calculate growth rates for all income metrics
    const fipsMedianIncomeGrowth = LTVCalculators.calculateCAGR(
        fipsCurrentIncomeMedian_2021,
        fipsCurrentIncomeMedian_2023
    );

    const fipsMeanIncomeGrowth = LTVCalculators.calculateCAGR(
        fipsCurrentIncomeMean_2021,
        fipsCurrentIncomeMean_2023
    );

    const zipMedianIncomeGrowth = LTVCalculators.calculateCAGR(
        zipCurrentIncomeMedian_2021,
        zipCurrentIncomeMedian
    );

    const zipMeanIncomeGrowth = LTVCalculators.calculateCAGR(
        zipCurrentIncomeMean_2021,
        zipCurrentIncomeMean
    );
    // console.log('Growth Rates', {
    //     'fipsMedianIncomeGrowth': fipsMedianIncomeGrowth,
    //     'fipsMeanIncomeGrowth': fipsMeanIncomeGrowth,
    //     'zipMedianIncomeGrowth': zipMedianIncomeGrowth,
    //     'zipMeanIncomeGrowth': zipMeanIncomeGrowth
    // })

    // Collect the growth rates into a single array
    const incomeGrowthRates = [fipsMedianIncomeGrowth, fipsMeanIncomeGrowth, zipMedianIncomeGrowth, zipMeanIncomeGrowth];
    // console.log('Income Growth Rates', incomeGrowthRates);

    // Filter out any growth rates that are less than 0
    const positiveIncomeGrowthRates = incomeGrowthRates.filter(rate => rate > 0);
    // console.log('Positive Income Growth Rates', positiveIncomeGrowthRates);

    // Get the average of the positive growth rates
    const averagePositiveIncomeGrowth = positiveIncomeGrowthRates.length > 0 ? positiveIncomeGrowthRates.reduce((sum, rate) => sum + rate, 0) / positiveIncomeGrowthRates.length : .02;
    // console.log('Average Positive Income Growth Rate', averagePositiveIncomeGrowth);

    // Set the steps above that average we may want to also show
    const averagePositiveIncomeGrowthPlus25 = averagePositiveIncomeGrowth + .25;
    const averagePositiveIncomeGrowthPlus50 = averagePositiveIncomeGrowth + .5;

    // Determine the max growth rate across all income metrics
    const maxIncomeGrowth = Math.max(fipsMedianIncomeGrowth, fipsMeanIncomeGrowth, zipMedianIncomeGrowth, zipMeanIncomeGrowth);

    // Take the current household income and model it out over next 10 years using the average / max growth rate
    const householdIncome10YearProjection = Array(10).fill(0).map((_, index) => {
        // Calculate compound growth: initial * (1 + rate)^year
        return Math.round(userFinancesHouseholdIncome * Math.pow(1 + averagePositiveIncomeGrowth, index + 1));
    });
    // console.log('Household Income 10 Year Projection', householdIncome10YearProjection);

    // Get the opex expenses as a percentage of the household income over the next 10 years
    const opexExpensesAsPctOfHouseholdIncome10YearProjection = opex10YearBreakdownAnnualTotal.map((expense, index) => {
        return (expense / householdIncome10YearProjection[index]);
    });
    // console.log('Opex Expenses as pct of household income 10 Year Projection', opexExpensesAsPctOfHouseholdIncome10YearProjection);

    // Get the min, max, mean, median, average, and growth rate of the opexExpensesAsPctOfHouseholdIncome10YearProjection array
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionMin = Math.min(...opexExpensesAsPctOfHouseholdIncome10YearProjection);
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionMax = Math.max(...opexExpensesAsPctOfHouseholdIncome10YearProjection);
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionMean = opexExpensesAsPctOfHouseholdIncome10YearProjection.reduce((sum, rate) => sum + rate, 0) / opexExpensesAsPctOfHouseholdIncome10YearProjection.length;
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionMedian = opexExpensesAsPctOfHouseholdIncome10YearProjection.sort((a, b) => a - b)[Math.floor(opexExpensesAsPctOfHouseholdIncome10YearProjection.length / 2)];
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionAverage = opexExpensesAsPctOfHouseholdIncome10YearProjection.reduce((sum, rate) => sum + rate, 0) / opexExpensesAsPctOfHouseholdIncome10YearProjection.length;
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionGrowthRate = Math.pow(
        opexExpensesAsPctOfHouseholdIncome10YearProjection[opexExpensesAsPctOfHouseholdIncome10YearProjection.length - 1] /
        opexExpensesAsPctOfHouseholdIncome10YearProjection[0],
        1 / (opexExpensesAsPctOfHouseholdIncome10YearProjection.length - 1)
    ) - 1;
    // console.log('Opex Expenses as pct of household income 10 Year Projection Growth Rate', opexExpensesAsPctOfHouseholdIncome10YearProjectionGrowthRate);
    
    // Get the index of the min, max, median, mean, and growth rate of the opexExpensesAsPctOfHouseholdIncome10YearProjection array
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionMinIndex = opexExpensesAsPctOfHouseholdIncome10YearProjection.indexOf(opexExpensesAsPctOfHouseholdIncome10YearProjectionMin);
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionMaxIndex = opexExpensesAsPctOfHouseholdIncome10YearProjection.indexOf(opexExpensesAsPctOfHouseholdIncome10YearProjectionMax);
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionMedianIndex = opexExpensesAsPctOfHouseholdIncome10YearProjection.indexOf(opexExpensesAsPctOfHouseholdIncome10YearProjectionMedian);
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionMeanIndex = opexExpensesAsPctOfHouseholdIncome10YearProjection.indexOf(opexExpensesAsPctOfHouseholdIncome10YearProjectionMean);
    const opexExpensesAsPctOfHouseholdIncome10YearProjectionGrowthRateIndex = opexExpensesAsPctOfHouseholdIncome10YearProjection.indexOf(opexExpensesAsPctOfHouseholdIncome10YearProjectionGrowthRate);

    // Create the opexRiskLevel
    let opexRiskLevel = 1;
    if (opexExpensesAsPctOfHouseholdIncome10YearProjectionMax < .3) {
        
        if (opexExpensesAsPctOfHouseholdIncome10YearProjectionMaxIndex < opexExpensesAsPctOfHouseholdIncome10YearProjectionMinIndex) {
            opexRiskLevel = 1;
        } else {
            opexRiskLevel = 3;
        }
    } else if (opexExpensesAsPctOfHouseholdIncome10YearProjectionMax < .35) {
        if (opexExpensesAsPctOfHouseholdIncome10YearProjectionMaxIndex < opexExpensesAsPctOfHouseholdIncome10YearProjectionMinIndex) {
            opexRiskLevel = 2;
        } else {
            opexRiskLevel = 4;
        }
    } else if (opexExpensesAsPctOfHouseholdIncome10YearProjectionMax < .4) {
        if (opexExpensesAsPctOfHouseholdIncome10YearProjectionMaxIndex < opexExpensesAsPctOfHouseholdIncome10YearProjectionMinIndex) {
            opexRiskLevel = 5;
        } else {
            opexRiskLevel = 6;
        }
    } else if (opexExpensesAsPctOfHouseholdIncome10YearProjectionMax < .45) {
        if (opexExpensesAsPctOfHouseholdIncome10YearProjectionMaxIndex < opexExpensesAsPctOfHouseholdIncome10YearProjectionMinIndex) {
            opexRiskLevel = 7;
        } else {
            opexRiskLevel = 8;
        }
    } else {
        if (opexExpensesAsPctOfHouseholdIncome10YearProjectionMaxIndex < opexExpensesAsPctOfHouseholdIncome10YearProjectionMinIndex) {
            opexRiskLevel = 9;
        } else {
            opexRiskLevel = 9;
        }
    }
    
    const opexOutputObject = {
        opexExpensesAsPctOfHouseholdIncome10YearProjectionMin,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionMax,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionMedian,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionMean,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionAverage,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionGrowthRate,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionMinIndex,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionMaxIndex,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionMedianIndex,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionMeanIndex,
        opexExpensesAsPctOfHouseholdIncome10YearProjectionGrowthRateIndex,
        opexExpensesAsPctOfHouseholdIncome10YearProjection,
        opexRiskLevel
    }

    return opexOutputObject;

}